import { eventBus, betslipUtility, capitalizeFirstLetter, ticketCheck } from '../utility';
import store from '../store';

const successTicketHandler = function (data) {
  eventBus.$emit('resetInput');
  store.dispatch('clearBetslip');
  const ticket = data.productTicket || data;
  const ticketVersion = ticketCheck.getTicketVersion(ticket);

  // Send message over GGateway event 'Tickets.Update' to update state of ticket
  const ticketData = {
    action: capitalizeFirstLetter(store.state.ticketAction), // available values: Add, Cancel, Payout
    ticket: ticketVersion === '2' ? betslipUtility.prepareSevenTicketData(ticket) : ticket,
  };
  eventBus.$emit('ticketUpdate', ticketData);

  // 8693v40hz
  if (store.state.ticketAction === 'Cancel' && data.status.value !== 'canceled') return;
  if (store.state.ticketAction === 'Payout' && data.status.value !== 'paidout') return;
  if (store.state.ticketActionSuccessful && data.status.value !== 'invalid') {
    // NOTE: Here we could add a part of the code from the *SuccessTicketCheckHandler function
    // that could help fix the double printing.
    eventBus.$emit('printTemplate', ticketData);
  }
};

const errorTicketCheckHandler = (error, ticketType, ticket) => {
  // Filter out axios network errors
  const networkErrorCodes = ['ERR_NETWORK', 'ECONNABORTED'];
  if (networkErrorCodes.includes(error.code)) {
    console.warn('NETWORK ERROR', error);
    return;
  }
  // console.log(' <====> errorTicketCheckHandler ', error);

  // Determine the correct handler based on ticketType
  const errorHandlers = {
    newTicket: aggregatorErrorTicketCheckHandler,
    ngsTicket: ngsErrorTicketCheckHandler,
  };

  const specificHandler = errorHandlers[ticketType];
  if (specificHandler) {
    specificHandler(error, ticket);
  } else {
    console.warn(`No error handler found for ticketType: ${ticketType}`, error);
  }
};

const aggregatorErrorTicketCheckHandler = (error, ticket) => {
  // console.log(' aggregatorErrorTicketCheckHandler ', error);

  const { action, requestUuid } = ticket || {};
  const normalizedAction = capitalizeFirstLetter(action === 'PayIn' ? 'Add' : action);

  const ticketData = {
    action: normalizedAction,
    ticket: {
      status: { value: 'REJECTED' },
      payin: store.getters.config.rules?.minBetAmount?.value,
      requestUuid,
    },
  };

  // Emit event if 404 or valid action
  // If error isn't from backend don't send ticket update
  // Covers case where ticketUpdate and ticketCheck responses are missing on ticket payin action
  if (error.response?.status === 404) {
    ticketData.showMessage = false;
    eventBus.$emit('ticketUpdate', ticketData);
  }

  const errorText = error.response?.data?.message || error.response?.data?.error || error.message;
  const errorMessage = store.getters.getTranslation(errorText);
  // Dispatch error message
  store.dispatch('sendGGMessage', { message: errorMessage, delay: 5000 });
};

const ngsErrorTicketCheckHandler = (error, ticket) => {
  // console.log(' ngsErrorTicketCheckHandler ', error);

  const { action, requestUuid } = ticket || {};
  const { response } = error;
  const normalizedAction = capitalizeFirstLetter(action === 'PayIn' ? 'Add' : action);

  const ticketData = {
    action: normalizedAction,
    ticket: {
      status: { value: 'REJECTED' },
      payin: store.getters.config.rules?.minBetAmount?.value,
      requestUuid,
    },
  };

  // Emit event if 404 or valid action
  // If error isn't from backend don't send ticket update
  // Covers case where ticketUpdate and ticketCheck responses are missing on ticket payin action
  // In case when ticket isn't found, platform will return 400, while ngs backend returns 404
  if (response?.status === 404 || (response?.status === 400 && !store.getters.ngsTicketCheck)) {
    ticketData.showMessage = false;
    eventBus.$emit('ticketUpdate', ticketData);
  }

  const errorText = error.response?.data?.message || error.response?.data?.error || error.message;
  const errorMessage = store.getters.getTranslation(errorText);
  // Dispatch error message
  store.dispatch('sendGGMessage', { message: errorMessage, delay: 5000 });
};

const errorTicketUpdateHandler = (error) => {
  // Filter out axios network errors
  const networkErrorCodes = ['ERR_NETWORK', 'ECONNABORTED'];
  if (networkErrorCodes.includes(error.code)) {
    console.warn('NETWORK ERROR', error);
    return;
  }
  // console.log(' errorTicketUpdateHandler ', error);

  // Check code length
  // If code is barcode (length 8), update ticket action status
  let barcode = error.id || error.code;
  if (!error.code || String(error.code).length !== 8) {
    barcode = null;
  }

  const { action, requestUuid, requestId, status, product } = error || {};
  const normalizedAction = capitalizeFirstLetter(action === 'PayIn' ? 'Add' : action);

  const ticketData = {
    action: normalizedAction,
    ticket: {
      status: { value: 'REJECTED' },
      requestUuid: status?.value === 'REJECTED' ? requestUuid : requestId,
      product,
      id: barcode,
    },
  };
  // Specific errors for which we must hide parent wrapper message
  // 400404 - missing guids
  // 7000 - betshop limit reached / must payout at betshop where ticket was paid in
  const specificNgsErrors = [400404, 7000];

  // Since there is no error code on aggregator, we can handle message
  const specificAggregatorErrors = ['ticket_error_same_betshop_payout'];

  if (specificNgsErrors.includes(error.code) || specificAggregatorErrors.includes(error.message))
    ticketData.showMessage = false;

  // Emit event if valid action
  if (normalizedAction) {
    eventBus.$emit('ticketUpdate', ticketData);
  }

  const errorText = error.response?.data?.message || error.response?.data?.error || error.message;
  const errorMessage = store.getters.getTranslation(errorText);

  // Dispatch error message
  store.dispatch('sendGGMessage', { message: errorMessage, delay: 5000 });
};

const successTicketCheckHandler = (data, ticketType) => {
  // console.log(' successTicketCheckHandler ', data);

  eventBus.$emit('resetInput');
  store.dispatch('clearBetslip');

  // Determine the correct handler based on ticketType
  const successHandlers = {
    newTicket: aggregatorSuccessTicketCheckHandler,
    ngsTicket: ngsSuccessTicketCheckHandler,
  };

  const specificHandler = successHandlers[ticketType];
  if (specificHandler) {
    specificHandler(data);
  } else {
    console.warn(`No success handler found for ticketType: ${ticketType}`);
  }
};
const aggregatorSuccessTicketCheckHandler = (data) => {
  // console.log(' aggregatorSuccessTicketCheckHandler ', data);

  const ticket = data.productTicket || data;
  const { ticketAction, ticketActionSuccessful } = store.state;
  const status = data.status.value;

  // Send message over GGateway event 'Tickets.Update' to update state of ticket
  const ticketData = {
    action: capitalizeFirstLetter(ticketAction), // available values: Add, Cancel, Payout
    ticket: betslipUtility.prepareSevenTicketData(ticket),
  };
  eventBus.$emit('ticketUpdate', ticketData);

  // 8693v40hz
  // Validate status based on current action
  if (ticketAction === 'Cancel' && status !== 'canceled') return;
  if (ticketAction === 'Payout' && status !== 'paidout') return;

  if (ticketActionSuccessful && status !== 'invalid') {
    const currentTicket = {
      id: data.id,
      requestUuid: data.requestUuid,
    };
    const { ticketsPrintedByTicketUpdate, printedTicketsByTicketCheck } = store.getters;
    // Check if in the meantime the ticket update caused ticket print
    if (ticketsPrintedByTicketUpdate.some((printedTicket) => printedTicket.requestUuid === currentTicket.requestUuid))
      return;
    if (printedTicketsByTicketCheck.some((printedTicket) => printedTicket.requestUuid === currentTicket.requestUuid))
      return;

    store.commit('SET_PRINTED_TICKET_BY_TICKET_CHECK', currentTicket);
    eventBus.$emit('printTemplate', ticketData);
  }
};
const ngsSuccessTicketCheckHandler = (data) => {
  // console.log(' ngsSuccessTicketCheckHandler ', data);

  const ticket = data.message || data;
  const { ticketAction, ticketActionSuccessful } = store.state;
  const { value: status } = ticket.status;

  // Send message over GGateway event 'Tickets.Update' to update state of ticket
  const ticketData = {
    action: capitalizeFirstLetter(ticketAction), // available values: Add, Cancel, Payout
    ticket,
  };

  eventBus.$emit('ticketUpdate', ticketData);
  // 8693v40hz
  // Validate status based on current action

  if (ticketAction === 'Cancel' && status !== 'CLOSED') return;
  if (ticketAction === 'Payout' && status !== 'PAYEDOUT') return;

  if (ticketActionSuccessful && status !== 'invalid') {
    const { ticketsPrintedByTicketUpdate, printedTicketsByTicketCheck } = store.getters;
    // Check if in the meantime the ticket update caused ticket print
    if (
      ticketsPrintedByTicketUpdate.some(
        (printedTicket) =>
          printedTicket.requestUuid === ticket.requestUuid && printedTicket.status.value === ticket.status.value,
      )
    )
      return;
    if (
      printedTicketsByTicketCheck.some(
        (printedTicket) =>
          printedTicket.requestUuid === ticket.requestUuid && printedTicket.status.value === ticket.status.value,
      )
    )
      return;

    store.commit('SET_PRINTED_TICKET_BY_TICKET_CHECK', ticket);
    eventBus.$emit('printTemplate', ticketData);
  }
};

const errorHandler = (error) => {
  let errorMessage;
  if (error.response) {
    // The server responded with a status code outside the 2xx range
    errorMessage = store.getters.getTranslation(
      error.response?.data?.message || error.response?.data?.error || error.message,
    );
  } else if (error.request) {
    errorMessage = 'Network error';
    // The request was made but no response was received
    console.log('Request got no response', error);
  } else {
    errorMessage = 'Network error';
    console.log('Request setup error', error);
    // Something happened in setting up the request that triggered an error
  }
  store.dispatch('sendGGMessage', { message: errorMessage, delay: 5000 });
};

export {
  successTicketHandler,
  successTicketCheckHandler,
  errorHandler,
  errorTicketUpdateHandler,
  errorTicketCheckHandler,
  ngsErrorTicketCheckHandler,
  aggregatorErrorTicketCheckHandler,
};
